// IndexedDB helpers for firmee PWA (identity + snapshots + outbox).

export const DB_NAME = "firmee-pwa"
export const DB_VERSION = 2
export const IDENTITY_KEY = "current"
export const SNAPSHOT_KEY = "active"
export const OUTBOX_STORE = "outbox"

function openDb() {
  if (typeof indexedDB === "undefined") {
    return Promise.reject(new Error("indexedDB is not available"))
  }
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)
    request.onerror = () => reject(request.error)
    request.onsuccess = () => resolve(request.result)
    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains("identity")) {
        db.createObjectStore("identity")
      }
      if (!db.objectStoreNames.contains("snapshots")) {
        db.createObjectStore("snapshots")
      }
      if (!db.objectStoreNames.contains(OUTBOX_STORE)) {
        db.createObjectStore(OUTBOX_STORE, { keyPath: "request_id" })
      }
    }
  })
}

function withStore(storeName, mode, fn) {
  return openDb().then(
    (db) =>
      new Promise((resolve, reject) => {
        db.onversionchange = () => {
          db.close?.()
        }

        const tx = db.transaction(storeName, mode)
        const store = tx.objectStore(storeName)

        const finish = (value) => {
          db.close?.()
          resolve(value)
        }
        const fail = (error) => {
          db.close?.()
          reject(error)
        }

        tx.onerror = () => fail(tx.error)
        tx.onabort = () => fail(tx.error)

        Promise.resolve(fn(store))
          .then((value) => {
            tx.oncomplete = () => finish(value)
          })
          .catch(fail)
      })
  )
}

export function idbGet(storeName, key) {
  return withStore(storeName, "readonly", (store) => {
    return new Promise((resolve, reject) => {
      const request = store.get(key)
      request.onsuccess = () => resolve(request.result || null)
      request.onerror = () => reject(request.error)
    })
  })
}

export function idbSet(storeName, key, value) {
  return withStore(storeName, "readwrite", (store) => {
    if (store.keyPath) {
      store.put(value)
    } else {
      store.put(value, key)
    }
  })
}

export function idbDelete(storeName, key) {
  return withStore(storeName, "readwrite", (store) => {
    store.delete(key)
  })
}

// identity の確認と書き込みを同一 transaction に置き、別タブの削除・再有効化と競合させない。
export async function idbSetForIdentity(identity, storeName, key, value) {
  const db = await openDb()
  return new Promise((resolve, reject) => {
    let written = false
    db.onversionchange = () => db.close?.()
    const tx = db.transaction([...new Set(["identity", storeName])], "readwrite")
    const request = tx.objectStore("identity").get(IDENTITY_KEY)
    request.onsuccess = () => {
      const current = request.result
      if (!current?.enabled || current.revision !== identity.revision) return
      tx.objectStore(storeName).put(value, key)
      written = true
    }
    tx.oncomplete = () => { db.close?.(); resolve(written) }
    tx.onerror = () => { db.close?.(); reject(tx.error) }
    tx.onabort = () => { db.close?.(); reject(tx.error) }
  })
}

export function idbClear(storeName) {
  return withStore(storeName, "readwrite", (store) => {
    store.clear()
  })
}

export function idbGetAll(storeName) {
  return withStore(storeName, "readonly", (store) => {
    return new Promise((resolve, reject) => {
      const request = store.getAll()
      request.onsuccess = () => resolve(request.result || [])
      request.onerror = () => reject(request.error)
    })
  })
}

export async function clearOfflineDatabase() {
  const db = await openDb()
  try {
    await Promise.all(
      ["identity", "snapshots", OUTBOX_STORE].map((name) => {
        if (!db.objectStoreNames.contains(name)) return Promise.resolve()
        return new Promise((resolve, reject) => {
          const tx = db.transaction(name, "readwrite")
          tx.objectStore(name).clear()
          tx.oncomplete = () => resolve()
          tx.onerror = () => reject(tx.error)
          tx.onabort = () => reject(tx.error)
        })
      })
    )
  } finally {
    db.close?.()
  }
}
